import { MouseEvent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Spinner, StandAloneLink, Surface } from '@vst/beam';
import { useTranslation } from 'react-i18next';

import { AEMContentProvider, createAemUrl } from '@app/components/AdobeExperienceFragments';
import { getAvailableNumbers, selectConfig, selectCurrentShopAddon, selectLocale, selectUserInfo } from '@app/redux';

import { AccountType } from '@mfe/shared/schema-types';

import AddonAemUnavailable from '../AddonAemUnavailable/addon-aem-unavailable';
import AddonCart from '../AddonCart';
import Footer from '../Footer';
import TermsAndConditions from '../TermsAndConditions';
import { getTermsAndConditions, mapAddonKindToAemUrlFragment } from '../../utils';
import { ADDON_PRODUCT_KIND } from '../../../../constants';

import styles from './index.module.scss';

export type SingleFlowProps = {
  handleBack: () => void;
  handleContinue: () => void;
  isDesktop?: boolean;
  continueButtonLabel: string;
  isLastStep?: boolean;
};

const SingleAddonContent = ({
  continueButtonLabel,
  handleBack,
  handleContinue,
  isDesktop,
  isLastStep,
}: SingleFlowProps) => {
  const { t } = useTranslation('ShopAddons');
  const dispatch = useDispatch();
  const {
    userInfo: {
      accountType,
      address: { service: serviceAddress },
    },
  } = useSelector(selectUserInfo);
  const {
    locale: { userLocale },
  } = useSelector(selectLocale);
  const { aemBaseUrl } = useSelector(selectConfig);
  const currentShopAddon = useSelector(selectCurrentShopAddon);
  const { kind } = currentShopAddon || {};
  const isSMB = accountType === AccountType.Smb;
  const isEasyCareForSMB = kind === ADDON_PRODUCT_KIND.easyCare && isSMB;
  const isCyberProtect = kind === ADDON_PRODUCT_KIND.cyberProtect;
  const isVoice = kind === ADDON_PRODUCT_KIND.voice;

  const shouldDisplayTermsAndConditions = !(isEasyCareForSMB || isCyberProtect);
  useEffect(() => {
    if (isVoice) {
      dispatch(getAvailableNumbers(serviceAddress?.postalCode as string));
    }
  }, [isVoice]);

  const [aemFragmentLoading, setAEMFragmentLoading] = useState(true);
  const [aemFragmentError, setAEMFragmentError] = useState(false);

  if (!currentShopAddon || !kind) {
    return null;
  }

  const aemURL = createAemUrl({
    baseUrl: aemBaseUrl,
    locale: userLocale,
    feature: mapAddonKindToAemUrlFragment(kind),
    accountType,
  });

  const termsAndConditionsContent = t(`termsAndConditions.${getTermsAndConditions(kind)}`);
  return (
    <div className={styles['container']}>
      <Surface className={styles['aem-fragment-surface']}>
        {aemFragmentLoading && (
          <div style={{ padding: '32px' }}>
            <Spinner label={t('cart.aemFragment.loading')} />
          </div>
        )}
        {aemFragmentError && !aemFragmentLoading ? (
          <div className={styles['addon-unavailable-wrapper']}>
            <AddonAemUnavailable />
          </div>
        ) : (
          <>
            <AEMContentProvider
              id={styles['aem-fragment-container']}
              style={{ display: aemFragmentLoading ? 'none' : 'block' }}
              src={aemURL}
              onLoad={() => setAEMFragmentLoading(false)}
              onError={() => {
                setAEMFragmentError(true);
              }}
            />
            <div
              style={{ display: aemFragmentLoading ? 'none' : 'block' }}
              className={`${styles['terms-container']} ${isEasyCareForSMB ? styles['easy-care'] : ''}`}
            >
              {shouldDisplayTermsAndConditions && <TermsAndConditions content={termsAndConditionsContent} />}
            </div>
          </>
        )}
        <Footer>
          <StandAloneLink
            variant={'primary'}
            linkSize={'medium'}
            href={'#'}
            data-cy="navigatorBackLink"
            onClick={(e: MouseEvent<HTMLElement>) => {
              handleBack();
              e.preventDefault();
            }}
          >
            {t('goBack')}
          </StandAloneLink>
          <Button variant="primary" onClick={handleContinue}>
            {continueButtonLabel}
          </Button>
        </Footer>
      </Surface>
      {isDesktop && (
        <AddonCart
          handleClick={handleContinue}
          addon={currentShopAddon}
          submitButtonLabel={continueButtonLabel}
          isLastStep={isLastStep}
          displayTC={shouldDisplayTermsAndConditions}
        />
      )}
    </div>
  );
};

export default SingleAddonContent;
